<template>
    <div v-if="shouldShowMore" class="show-more-by-item-count">
        <ShowMoreLocked
            v-if="showMoreLock && locked"
            :label="locked.label"
            :test-id="testId"
            :href="lockedHref"
            :size="size"
            @click="handleClickedOnLocked"
        />
        <ShowMore v-else :collapsed="collapsed" :label="label" :test-id="testId" :size="size" @click="handleClick" />
    </div>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {computed, ref, useRoute} from '#imports';
    import {type TJoyButtonSizes} from '@maltjoy/core-vue';
    import ShowMore from '~/components/_common/show-more/ShowMore.vue';
    import ShowMoreLocked from '~/components/_common/show-more/ShowMoreLocked.vue';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';

    export type ShowMoreByItemCountProps = {
        label?: string;
        allowLockedHref?: boolean;
        testId: string;
        total: number;
        limit: number;
        size?: TJoyButtonSizes;
        locked?:
            | {allowHref: false; label: string} // No href, event sent
            | {allowHref: true; label: string; componentName: string; targetId: string}; // default href
    };
    const props = defineProps<ShowMoreByItemCountProps>();
    const emits = defineEmits<{
        (e: 'expand'): void;
        (e: 'collapse'): void;
        (e: 'showMoreClickedWhenLocked'): void;
    }>();

    const shouldShowMore = computed(() => props.total > props.limit);

    // Locked
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const showMoreLock = computed(() => sectionVisibility.value[SectionsVisibilityType.VIEW_MORE_LOCK]);
    const route = useRoute();
    const lockedHref = computed(() => {
        if (!props.locked || !props.locked.allowHref) {
            return undefined;
        }
        const redirectParam = encodeURIComponent(`/profile${route.path}#${props.locked.targetId}`);
        return `/who-are-you?redirect=${redirectParam}&pageSource=profile_page&componentSource=${props.locked.componentName}`;
    });
    function handleClickedOnLocked() {
        emits('showMoreClickedWhenLocked');
    }

    // Connected
    const collapsed = ref<boolean>(true);
    function handleClick() {
        if (collapsed.value) {
            collapsed.value = false;
            emits('expand');
        } else {
            collapsed.value = true;
            emits('collapse');
        }
    }
</script>

<style lang="scss">
    .show-more-by-item-count {
        margin-top: var(--joy-core-spacing-3);
    }
</style>
